/*
  Previous footer implementation retained for reference.
  The new landing-page footer below replaces it to match the refreshed LuckyAds design language.
*/

import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo';
import { useLocales } from 'src/locales';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';

const footerColumns = [
  {
    headline: 'LuckyAds',
    links: [
      { label: 'About us', href: '#about-us' },
      { label: 'How it works', href: '#how-it-works' },
      { label: 'Active giveaways', href: '#active-giveaway' },
    ],
  },
  {
    headline: 'Company',
    links: [
      { label: 'Contact us', href: paths.contact },
      { label: 'Privacy policy', href: paths.privacy },
      { label: 'Terms & conditions', href: paths.termsOfService },
    ],
  },
  {
    headline: 'Follow',
    links: [
      { label: 'Instagram', href: 'https://www.instagram.com/luckyadscom', external: true },
      { label: 'Facebook', href: 'https://www.facebook.com/luckyadscom', external: true },
      { label: 'LinkedIn', href: 'https://www.linkedin.com/company/luckyadscom', external: true },
    ],
  },
];

const socials = [
  { name: 'Instagram', icon: 'skill-icons:instagram', color: '#E02D69', path: 'https://www.instagram.com/luckyadscom' },
  { name: 'Facebook', icon: 'eva:facebook-fill', color: '#1877F2', path: 'https://www.facebook.com/luckyadscom' },
  { name: 'LinkedIn', icon: 'eva:linkedin-fill', color: '#0A66C2', path: 'https://www.linkedin.com/company/luckyadscom' },
  { name: 'X', icon: 'logos:twitter', color: '#050505', path: 'https://twitter.com/luckyadscom' },
];

export default function Footer() {
  const theme = useTheme();
  const { t } = useLocales();
  const isLight = theme.palette.mode === 'light';
  const accent = isLight ? '#ff5c35' : '#42e9ff';
  const highlight = isLight ? '#d9f53f' : '#ee4dff';

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        color: isLight ? '#1d1b18' : '#f5f5ff',
        background: isLight ? '#f3efe8' : '#070817',
        borderTop: `1px solid ${alpha(isLight ? '#1d1b18' : '#ffffff', isLight ? 0.1 : 0.12)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isLight
            ? 'radial-gradient(circle at 20% 0%, rgba(255, 92, 53, 0.16), transparent 26%), radial-gradient(circle at 90% 10%, rgba(217, 245, 63, 0.22), transparent 28%)'
            : 'radial-gradient(circle at 20% 0%, rgba(66, 233, 255, 0.12), transparent 22%), radial-gradient(circle at 90% 10%, rgba(238, 77, 255, 0.14), transparent 22%)',
          pointerEvents: 'none',
        }}
      />

      <Container sx={{ position: 'relative', py: { xs: 5, md: 7 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: `1px solid ${alpha(accent, 0.2)}`,
            background: isLight ? 'rgba(255,255,255,0.7)' : 'rgba(15, 18, 36, 0.74)',
            boxShadow: isLight ? '0 22px 52px rgba(13, 15, 18, 0.08)' : '0 22px 52px rgba(0, 0, 0, 0.26)',
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', md: 520 } }}>
            <Typography variant="overline" sx={{ color: accent, fontWeight: 900, letterSpacing: 2.4 }}>
              {t('home.luckyads') || 'LuckyAds'}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.05 }}>
              Ready for your next win?
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              component={RouterLink}
              to={paths.auth.amplify.register}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 800,
                background: accent,
                color: '#1d1b18',
                boxShadow: '0 12px 28px rgba(0,0,0,0.16)',
                '&:hover': { background: highlight, color: '#1d1b18' },
              }}
            >
              Enter to win
            </Button>
            <Button
              component={RouterLink}
              to={paths.contact}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 800,
                color: isLight ? '#1d1b18' : '#ffffff',
                borderColor: alpha(accent, 0.35),
                '&:hover': { borderColor: accent, background: alpha(accent, 0.05) },
              }}
            >
              Talk to us
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={4} sx={{ mt: 1.5 }}>
          <Grid xs={12} md={4}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 2.5 }}>
              <Logo sx={{ width: 82, height: 82 }} />
            </Box>
            <Typography sx={{ maxWidth: 360, color: isLight ? '#4d4945' : '#c9d0e8', lineHeight: 1.8, fontSize: 15 }}>
              Watch short ads, collect entries, and unlock premium giveaways from leading lifestyle, tech, and travel brands.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              {socials.map((social) => (
                <Link key={social.name} href={social.path} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex' }}>
                  <IconButton
                    sx={{
                      background: isLight ? '#ffffff' : '#101827',
                      border: `1px solid ${alpha(accent, 0.2)}`,
                      color: social.color,
                      '&:hover': { background: alpha(social.color, 0.12) },
                    }}
                  >
                    <Iconify icon={social.icon} width={18} />
                  </IconButton>
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid xs={12} md={8}>
            <Grid container spacing={3.5}>
              {footerColumns.map((column) => (
                <Grid key={column.headline} xs={12} sm={4}>
                  <Typography variant="overline" sx={{ display: 'block', color: accent, fontWeight: 900, letterSpacing: 1.6, mb: 2 }}>
                    {column.headline}
                  </Typography>

                  <Stack spacing={1.2}>
                    {column.links.map((link) => (
                      <Link
                        key={link.label}
                        component={link.external ? 'a' : RouterLink}
                        to={link.external ? undefined : link.href}
                        href={link.external ? link.href : undefined}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noreferrer' : undefined}
                        sx={{
                          color: isLight ? '#403d39' : '#e5e7ff',
                          textDecoration: 'none',
                          fontSize: 15,
                          transition: 'color 0.2s ease',
                          '&:hover': { color: accent },
                        }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: alpha(isLight ? '#1d1b18' : '#ffffff', 0.12) }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Typography variant="body2" sx={{ color: isLight ? '#5e5b57' : '#b7bfd8' }}>
            © {new Date().getFullYear()} LuckyAds. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={2.5}>
            <Link component={RouterLink} to={paths.privacy} sx={{ color: isLight ? '#5e5b57' : '#b7bfd8', textDecoration: 'none', '&:hover': { color: accent } }}>
              Privacy
            </Link>
            <Link component={RouterLink} to={paths.termsOfService} sx={{ color: isLight ? '#5e5b57' : '#b7bfd8', textDecoration: 'none', '&:hover': { color: accent } }}>
              Terms
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
